import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import MaterialReactTable from 'material-react-table'
import moment from 'moment'
import { Button, Col, FormGroup, Input, Label, Row, Spinner } from 'reactstrap'
import { ButtonLiberarPagamento } from '../../components/Modals/ModalContrFornInfo/ButtonLiberarPagamento'
import AddFilesNoForm from '../../components/Template/Files/AddFilesNoForm'
import MaskedInput from '../../components/Template/Input/MaskedInput'
import DatePicker from 'react-datepicker'
import oikosApi from '../../services/oikos'
import { notifyError, notifySuccess } from '../../utils/notify'
import { formatPgtoPrazoDiasDisplay, persistPgtoPrazoDias } from '../../components/Modals/ModalFormContrForn/contrFornEntregaveisUtils'
import { MEIO_PGTO } from './contratosTabHelpers'

const toCents = (value) => Math.round((Number(value) || 0) * 100)

const eventoNumValue = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY
}

const sortParcelasByNumero = (parcelas = []) => (
  [...parcelas].sort((a, b) => eventoNumValue(a.contrFornEvento_num) - eventoNumValue(b.contrFornEvento_num))
)

const ContrFornParcelasTable = ({
  data,
  isLoading = false,
  contrFornData = {},
  fornecedor = {},
  onParcelasChange,
  getEventos,
  updateOneEvento,
  onContrFornParteUpdated,
  setFornecedor,
}) => {
  const parcelas = Array.isArray(data) ? data : []
  const sortedData = useMemo(() => sortParcelasByNumero(parcelas), [parcelas])

  const handleDetailChange = (eventoId, patch) => {
    if (!onParcelasChange) return
    onParcelasChange(
      parcelas.map((item) => (
        item.contrFornEvento_id === eventoId ? { ...item, ...patch } : item
      )),
    )
  }

  const handleUpdateEvento = async (evento) => {
    const valorGlobalEmCentavos = toCents(contrFornData.contrForn_vlrGlobal)
    const totalParcelasEmCentavos = parcelas.reduce(
      (total, parcela) => total + toCents(parcela.contrFornEvento_vlr),
      0,
    )

    if (totalParcelasEmCentavos > valorGlobalEmCentavos) {
      notifyError('A soma das parcelas não pode ultrapassar o valor global do contrato.')
      return
    }

    const hasDtPrev = Boolean(evento.contrFornEvento_dtPrev)
    const pgtoPrazoDiasPersist = persistPgtoPrazoDias(evento.contrFornEvento_pgtoPrazoDias)

    try {
      await oikosApi.post('/', {
        action: 'update',
        table: 'contrFornEvento',
        data: {
          contrFornEvento_id: evento.contrFornEvento_id,
          contrFornEvento_desc: evento.contrFornEvento_desc,
          contrFornEvento_vlr: evento.contrFornEvento_vlr,
          // API Oikos não grava NULL (vira ''); omitir campos vazios.
          ...(hasDtPrev ? {
            contrFornEvento_dtPrev: moment(evento.contrFornEvento_dtPrev).format('YYYY-MM-DD'),
          } : {}),
          ...(pgtoPrazoDiasPersist !== undefined ? {
            contrFornEvento_pgtoPrazoDias: pgtoPrazoDiasPersist,
          } : {}),
        },
      })
      notifySuccess('Evento atualizado com sucesso!')
    } catch (error) {
      console.log(error)
      notifyError('Não foi possível atualizar o evento.')
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'contrFornEvento_num',
      header: 'Número',
      size: 100,
      maxWidth: 200,
      sortingFn: (rowA, rowB) => (
        eventoNumValue(rowA.original.contrFornEvento_num) - eventoNumValue(rowB.original.contrFornEvento_num)
      ),
      Cell: ({ row }) => row.original.contrFornEvento_num || 'Sem número',
    },
    {
      accessorKey: 'contrFornEvento_desc',
      header: 'Descrição',
      size: 100,
      maxWidth: 200,
      accessorFn: (row) => {
        if (row.contrFornEvento_desc) {
          return row.contrFornEvento_desc
        }
        return 'Sem Descrição'
      },
    },
    {
      accessorKey: 'contrFornEvento_pgtoPrazoDias',
      header: 'Prazo (Dias)',
      size: 100,
      maxWidth: 200,
      accessorFn: (row) => formatPgtoPrazoDiasDisplay(row.contrFornEvento_pgtoPrazoDias),
    },
    {
      accessorKey: 'contrFornEvento_dtPrev',
      header: 'Data prevista',
      accessorFn: (row) => {
        if (row.contrFornEvento_dtPrev) {
          return moment(row.contrFornEvento_dtPrev, 'YYYY-MM-DD').toDate()
        }
        return null
      },
      size: 100,
      sortingFn: 'datetime',
      Cell: ({ cell, row }) => {
        if (row.original.contrFornEvento_dtPrev) {
          return moment(cell.getValue()).format('DD/MM/YYYY')
        }
        return null
      },
    },
    {
      accessorKey: 'contrFornEvento_vlr',
      header: 'Valor',
      size: 100,
      maxWidth: 200,
      accessorFn: (row) => {
        if (row.contrFornEvento_vlr) {
          return parseFloat(row.contrFornEvento_vlr).toFixed(2)
        }
        return '0.00'
      },
      Cell: ({ cell }) => {
        const value = cell.getValue()
        return value ? `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}` : 'Sem Valor'
      },
    },
    {
      id: 'status_bordero',
      header: 'Borderô',
      size: 170,
      accessorFn: (row) => {
        if (!row.bdro_id) {
          if (
            Number(row.contrFornEvento_aprovLiberado) === 1
            && Number(row.meioPgto_id) === MEIO_PGTO.CARTAO
          ) {
            const dtPago = row.contrFornEvento_dtReal || moment().format('YYYY-MM-DD')
            return `Pago em ${moment(dtPago).format('DD/MM/YYYY')}`
          }
          return 'Não lançado'
        }

        if (row.bdro_dtQuit) {
          return `Pago em ${moment(row.bdro_dtQuit).format('DD/MM/YYYY')}`
        }

        if (row.bdro_dtVenc) {
          return `Previsto para ${moment(row.bdro_dtVenc).format('DD/MM/YYYY')}`
        }

        return 'Não lançado'
      },
    },
    {
      id: 'acoes_parcela',
      header: 'Acões',
      size: 100,
      maxWidth: 200,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const parcela = row.original
        const statusId = Number(parcela.contrFornStatus_id ?? contrFornData.contrFornStatus_id)

        return (
          <div className="d-flex">
            {statusId === 2 && (
              <ButtonLiberarPagamento
                fornecedor={fornecedor}
                setFornecedor={setFornecedor}
                contrFornEventoData={parcela}
                getEventos={getEventos}
                listaParcelas={sortedData}
                setListaParcelas={onParcelasChange}
                contrFornData={contrFornData}
                updateOneEvento={updateOneEvento}
                onContrFornParteUpdated={onContrFornParteUpdated}
              />
            )}
          </div>
        )
      },
    },
  ], [
    contrFornData,
    fornecedor,
    getEventos,
    onContrFornParteUpdated,
    onParcelasChange,
    setFornecedor,
    sortedData,
    updateOneEvento,
  ])

  if (isLoading) {
    return (
      <div className="d-flex align-items-center py-2">
        <Spinner size="sm" color="primary" className="mr-2" />
        <span>Carregando parcelas...</span>
      </div>
    )
  }

  if (!parcelas.length) {
    return <span className="py-2 d-inline-block">Sem parcelas cadastradas.</span>
  }

  return (
    <MaterialReactTable
      columns={columns}
      data={sortedData}
      getRowId={(row) => String(row.contrFornEvento_id)}
      initialState={{
        density: 'compact',
        expanded: {},
        pagination: { pageIndex: 0, pageSize: 50 },
        sorting: [{ id: 'contrFornEvento_num', desc: false }],
      }}
      enableGrouping={false}
      enableTopToolbar={false}
      enableGlobalFilter
      enableFilters
      muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
      enablePagination={sortedData.length > 50}
      muiTopToolbarProps={{ sx: { '.MuiBox-root': { marginTop: '10px', height: '20px', display: 'flex', alignItems: 'center' }, '.MuiSvgIcon-root': { width: '12px' }, '.MuiButtonBase-root': { padding: '5px 5px 5px 5px' } } }}
      muiTableBodyCellProps={{ sx: { fontSize: '13px' } }}
      muiTablePaginationProps={{
        sx: { '.MuiTablePagination-displayedRows': { fontSize: '12px' }, marginTop: '0px', fontSize: '12px' },
        labelRowsPerPage: <span style={{ fontSize: '12px' }}>Produtos por página</span>,
        rowsPerPageOptions: [50, 100],
      }}
      positionPagination="top"
      muiTableHeadCellColumnActionsButtonProps={{ size: '8px' }}
      enableRowExpansion
      enableDensityToggle={false}
      enableColumnFilters={false}
      muiTableHeadCellProps={{
        sx: {
          '.MuiSvgIcon-root': { fontSize: '18px' },
          fontSize: '12px',
          fontWeight: 'normal',
          padding: '8px',
        },
      }}
      renderDetailPanel={({ row }) => {
        const p = row.original
        return (
          <Box p={2}>
            <Row>
              <Col md={4} className="pt-4">
                <FormGroup>
                  <Label for={`desc-${p.contrFornEvento_id}`}>Descrição</Label>
                  <Input
                    id={`desc-${p.contrFornEvento_id}`}
                    value={p.contrFornEvento_desc ?? ''}
                    onChange={(e) => handleDetailChange(p.contrFornEvento_id, { contrFornEvento_desc: e.target.value })}
                  />
                </FormGroup>
              </Col>

              <Col md={4} className="pt-4">
                <FormGroup>
                  <Label for={`vlr-${p.contrFornEvento_id}`}>Valor</Label>
                  <MaskedInput
                    disabled
                    unmask
                    mask="num"
                    blocks={{
                      num: {
                        mask: Number,
                        scale: 2,
                        signed: false,
                        thousandsSeparator: '.',
                        radix: ',',
                        min: 0,
                      },
                    }}
                    value={p.contrFornEvento_vlr ? String(p.contrFornEvento_vlr) : null}
                    onAccept={(value) => handleDetailChange(p.contrFornEvento_id, { contrFornEvento_vlr: Number(value) })}
                  />
                </FormGroup>
              </Col>

              <Col className="d-flex align-items-center pt-4" sm={4}>
                <span className="mr-2">A parcela depende da execução de uma atividade?</span>
                <input
                  type="checkbox"
                  name={`data-fixa-${p.contrFornEvento_id}`}
                  id={`data-fixa-${p.contrFornEvento_id}`}
                  checked={p.contrFornEvento_dependeAtividade === 1}
                  onChange={(e) => {
                    handleDetailChange(p.contrFornEvento_id, {
                      contrFornEvento_dependeAtividade: e.target.checked ? 1 : 0,
                      ...(e.target.checked ? {} : {
                        contrFornEvento_dtPrev: null,
                        contrFornEvento_pgtoPrazoDias: null,
                      }),
                    })
                  }}
                />
              </Col>

              {p.contrFornEvento_dependeAtividade === 1 ? (
                <>
                  <Col sm={4} className="pt-4">
                    <FormGroup>
                      <Label>
                        Data Prevista p/ realização da atividade
                      </Label>
                      <DatePicker
                        placeholderText="__/__/_____"
                        locale="pt-BR"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        popperContainer={() => <></>}
                        dropdownMode="select"
                        selected={p.contrFornEvento_dtPrev ? moment(p.contrFornEvento_dtPrev).toDate() : null}
                        onChange={(date) => {
                          handleDetailChange(p.contrFornEvento_id, {
                            contrFornEvento_dtPrev: date ? moment(date).format('YYYY-MM-DD') : null,
                          })
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4} className="pt-4">
                    <FormGroup>
                      <Label for={`prazo-${p.contrFornEvento_id}`}>Prazo p/ pagamento após a realização da atividade (dias)</Label>
                      <Input
                        type="number"
                        id={`prazo-${p.contrFornEvento_id}`}
                        value={p.contrFornEvento_pgtoPrazoDias ?? ''}
                        onChange={(e) => handleDetailChange(p.contrFornEvento_id, { contrFornEvento_pgtoPrazoDias: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </>
              ) : (
                <Col sm={4} className="pt-4">
                  <FormGroup>
                    <Label>
                      Data Prevista p/ realização do pagamento
                    </Label>
                    <DatePicker
                      placeholderText="__/__/_____"
                      locale="pt-BR"
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      popperContainer={() => <></>}
                      dropdownMode="select"
                      selected={p.contrFornEvento_dtPrev ? moment(p.contrFornEvento_dtPrev).toDate() : null}
                      onChange={(date) => {
                        handleDetailChange(p.contrFornEvento_id, {
                          contrFornEvento_dtPrev: date ? moment(date).format('YYYY-MM-DD') : null,
                        })
                      }}
                    />
                  </FormGroup>
                </Col>
              )}

              {contrFornData.contrFornTp_id !== 1 && contrFornData.contrFornTp_id !== 5 && contrFornData.contrFornTp_id !== 14 && (
                <Col className="d-flex align-items-center pt-4" sm={4}>
                  <span className="mr-2">A parcela possui liberação automática?</span>
                  <input
                    type="checkbox"
                    name={`liberacao-automatica-${p.contrFornEvento_id}`}
                    id={`liberacao-automatica-${p.contrFornEvento_id}`}
                    checked={p.contrFornEvento_liberacaoAutomatica === 1}
                    onChange={(e) => {
                      handleDetailChange(p.contrFornEvento_id, {
                        contrFornEvento_liberacaoAutomatica: e.target.checked ? 1 : 0,
                        ...(e.target.checked ? {} : { contrFornEvento_docCobranca: [] }),
                      })
                    }}
                  />
                </Col>
              )}

              {p.contrFornEvento_liberacaoAutomatica === 1 && (
                <Col md={4} className="pt-4">
                  <FormGroup>
                    <Label for={`doc-cobranca-${p.contrFornEvento_id}`}>Adicione o documento de cobrança</Label>
                    <AddFilesNoForm
                      multiple={false}
                      files={p.contrFornEvento_docCobranca}
                      onFilesChange={(e) => handleDetailChange(p.contrFornEvento_id, { contrFornEvento_docCobranca: e })}
                    />
                  </FormGroup>
                </Col>
              )}

              <Button onClick={() => handleUpdateEvento(p)}>Salvar alterações</Button>
            </Row>
          </Box>
        )
      }}
    />
  )
}

export default ContrFornParcelasTable
